import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { createRoom, joinRoom, getRoom, startRoom, getLeaderboard, getProfile, ApiError } from "../api/client"
import { haptic, buildBotStartLink, shareLink } from "../telegram/webApp"

// Authentication + the join-link redirect now happen once in App.tsx's
// AppShell (before any route renders), so this page only needs to render
// the menu.
export function HomePage() {
	const navigate = useNavigate()

	return (
		<div className="page home-page">
			<h1>🎮 بازی منچ</h1>
			<button onClick={() => navigate("/create")}>🎮 ساخت بازی</button>
			<button onClick={() => navigate("/join")}>👥 پیوستن به بازی</button>
			<button onClick={() => navigate("/profile")}>📊 پروفایل</button>
			<button onClick={() => navigate("/leaderboard")}>🏆 جدول امتیازات</button>
		</div>
	)
}

export function CreateRoomPage() {
	const navigate = useNavigate()
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		createRoom()
			.then((res) => navigate(`/lobby/${res.roomCode}`))
			.catch((err) => setError(err instanceof ApiError ? err.message : "Failed to create room"))
	}, [navigate])

	return <div className="page">{error ?? "Creating room…"}</div>
}

export function JoinRoomPage() {
	const { roomCode: paramCode } = useParams()
	const navigate = useNavigate()
	const [code, setCode] = useState(paramCode ?? "")
	const [error, setError] = useState<string | null>(null)

	const doJoin = async (roomCode: string) => {
		try {
			await joinRoom(roomCode)
			navigate(`/lobby/${roomCode}`)
		} catch (err) {
			setError(err instanceof ApiError ? err.message : "Failed to join room")
		}
	}

	useEffect(() => {
		if (paramCode) doJoin(paramCode)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paramCode])

	if (paramCode) return <div className="page">{error ?? "Joining room…"}</div>

	return (
		<div className="page join-page">
			<h2>کد اتاق را وارد کنید</h2>
			<input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={6} />
			<button onClick={() => doJoin(code)}>ورود</button>
			{error && <p className="error-text">{error}</p>}
		</div>
	)
}

export function LobbyPage() {
	const { roomCode = "" } = useParams()
	const navigate = useNavigate()
	const [room, setRoom] = useState<{ hostUserId: string; maxPlayers: number; state: { players: Array<{ userId: string; username: string; color: string }> } } | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [inviteNotice, setInviteNotice] = useState<string | null>(null)

	useEffect(() => {
		const poll = setInterval(() => {
			getRoom(roomCode)
				.then((res: any) => {
					setRoom(res)
					if (res.status === "IN_PROGRESS") navigate(`/game/${roomCode}`)
				})
				.catch((err) => setError(err instanceof ApiError ? err.message : "failed to load room"))
		}, 2000)
		return () => clearInterval(poll)
	}, [roomCode, navigate])

	const players = room?.state?.players ?? []

	const onInvite = async () => {
		const link = buildBotStartLink(`join_${roomCode}`)
		if (!link) {
			setInviteNotice("لینک دعوت در دسترس نیست — نام کاربری بات تنظیم نشده")
			return
		}
		const result = await shareLink(link, "بیا با هم بازی منچ کنیم!")
		haptic("light")
		if (result === "copied") setInviteNotice("لینک دعوت کپی شد")
		else if (result === "failed") setInviteNotice(link)
		else setInviteNotice(null)
	}

	return (
		<div className="page lobby-page">
			<h2>Room {roomCode}</h2>
			{players.map((p) => (
				<div key={p.userId} className={`player-row ${p.color.toLowerCase()}`}>{p.username}</div>
			))}
			<button disabled={players.length < 2} onClick={() => startRoom(roomCode).then(() => haptic())}>
				شروع بازی
			</button>
			{players.length < 2 && <p className="hint">برای شروع حداقل به ۲ بازیکن نیاز است — منتظر پیوستن دوستان بمان</p>}
			<button onClick={onInvite}>دعوت دوستان</button>
			{inviteNotice && <p className="hint">{inviteNotice}</p>}
			{error && <p className="error-text">{error}</p>}
		</div>
	)
}

export function LeaderboardPage() {
	const [rows, setRows] = useState<Array<{ rank: number; first_name: string; rating: number; games_won: number; games_played: number }>>([])
	useEffect(() => { getLeaderboard().then((res) => setRows(res.leaderboard)) }, [])
	return (
		<div className="page leaderboard-page">
			<h2>🏆 Leaderboard</h2>
			<table>
				<thead><tr><th>#</th><th>Name</th><th>Rating</th><th>Wins</th><th>Games</th></tr></thead>
				<tbody>
					{rows.map((r) => (
						<tr key={r.rank}><td>{r.rank}</td><td>{r.first_name}</td><td>{r.rating}</td><td>{r.games_won}</td><td>{r.games_played}</td></tr>
					))}
				</tbody>
			</table>
		</div>
	)
}

export function ProfilePage() {
	const [data, setData] = useState<{ user: { first_name: string; rating: number; games_played: number; games_won: number; games_lost: number }; recentGames: unknown[] } | null>(null)
	useEffect(() => { getProfile().then(setData) }, [])
	if (!data) return <div className="page">Loading…</div>
	return (
		<div className="page profile-page">
			<h2>{data.user.first_name}</h2>
			<p>Rating: {data.user.rating}</p>
			<p>Games: {data.user.games_played} • Wins: {data.user.games_won} • Losses: {data.user.games_lost}</p>
			<h3>Recent games</h3>
			<ul>{data.recentGames.map((g: any) => <li key={g.id}>{g.status} — {g.started_at}</li>)}</ul>
		</div>
	)
}

export function ResultPage() {
	const navigate = useNavigate()
	return (
		<div className="page result-page">
			<button onClick={() => navigate("/create")}>🎮 Rematch</button>
			<button onClick={() => navigate("/")}>🏠 Main Menu</button>
		</div>
	)
}